// Thin promise-based wrapper over amazon-cognito-identity-js. SRP auth against
// the Cognito user pool; email is the username (verified against the deployed
// pool). Only imported when syncConfig is present.
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { syncConfig } from '../config';

export interface AuthUser {
  email: string;
  sub: string;
  /** Cognito's `given_name`. Empty for accounts created before we asked for it. */
  firstName: string;
  /** Member of the Cognito "admins" group (may edit poses / generate images). */
  isAdmin: boolean;
}

let pool: CognitoUserPool | null = null;
function userPool(): CognitoUserPool {
  if (!syncConfig) throw new Error('Sync is not configured.');
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: syncConfig.userPoolId,
      ClientId: syncConfig.userPoolClientId,
    });
  }
  return pool;
}

function cognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: userPool() });
}

/** Cognito surfaces errors as { code, message }; expose a friendly message. */
function friendly(err: unknown): Error {
  const e = err as { code?: string; message?: string };
  const map: Record<string, string> = {
    UsernameExistsException: 'An account with that email already exists.',
    NotAuthorizedException: 'Incorrect email or password.',
    UserNotFoundException: 'Incorrect email or password.',
    UserNotConfirmedException: 'Please confirm your email first.',
    CodeMismatchException: 'That confirmation code is not correct.',
    ExpiredCodeException: 'That confirmation code has expired.',
    InvalidPasswordException: 'Password must be at least 8 characters with a letter and a number.',
    InvalidParameterException: 'Please check the details you entered.',
    LimitExceededException: 'Too many attempts. Please wait a while and try again.',
  };
  return new Error((e.code && map[e.code]) || e.message || 'Something went wrong.');
}

export function signUp(
  email: string,
  password: string,
  firstName = '',
): Promise<{ needsConfirmation: boolean }> {
  return new Promise((resolve, reject) => {
    const attrs = [new CognitoUserAttribute({ Name: 'email', Value: email })];
    // `given_name` is an optional, mutable standard attribute that already
    // exists on the pool, so greeting people by name needs no schema change.
    const given = firstName.trim();
    if (given) attrs.push(new CognitoUserAttribute({ Name: 'given_name', Value: given }));
    userPool().signUp(email, password, attrs, [], (err, result) => {
      if (err) return reject(friendly(err));
      resolve({ needsConfirmation: !result?.userConfirmed });
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmRegistration(code, true, (err) => {
      if (err) return reject(friendly(err));
      resolve();
    });
  });
}

export function resendCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((err) => {
      if (err) return reject(friendly(err));
      resolve();
    });
  });
}

/** Start a password reset — Cognito emails a verification code. */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).forgotPassword({
      onSuccess: () => resolve(),
      inputVerificationCode: () => resolve(), // code sent — proceed to the reset step
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

/** Finish a password reset with the emailed code and a new password. */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

export function signIn(email: string, password: string): Promise<AuthUser> {
  return new Promise((resolve, reject) => {
    const user = cognitoUser(email);
    const details = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(userFromSession(session, email)),
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

export function signOut(): void {
  userPool().getCurrentUser()?.signOut();
}

/** Resolve the persisted session (auto-refreshes if needed), or null. */
export function currentUser(): Promise<AuthUser | null> {
  const user = userPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(userFromSession(session));
    });
  });
}

/** A valid ID token for API calls (auto-refreshes), or null if signed out. */
export function idToken(): Promise<string | null> {
  const user = userPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/**
 * Set (or clear) the signed-in user's first name. Tokens are minted at sign-in,
 * so the session is refreshed afterwards — otherwise the new `given_name` claim
 * wouldn't reach the ID token until the next sign-in.
 */
export function updateFirstName(firstName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = userPool().getCurrentUser();
    if (!user) return reject(new Error('You are not signed in.'));
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return reject(new Error('Your session has expired.'));
      const attrs = [new CognitoUserAttribute({ Name: 'given_name', Value: firstName.trim() })];
      user.updateAttributes(attrs, (updateErr) => {
        if (updateErr) return reject(friendly(updateErr));
        user.refreshSession(session.getRefreshToken(), (refreshErr) => {
          if (refreshErr) return reject(friendly(refreshErr));
          resolve();
        });
      });
    });
  });
}

function userFromSession(session: CognitoUserSession, fallbackEmail = ''): AuthUser {
  const claims = session.getIdToken().decodePayload() as {
    sub: string;
    email?: string;
    given_name?: string;
    'cognito:groups'?: string[];
  };
  const groups = claims['cognito:groups'] ?? [];
  return {
    sub: claims.sub,
    email: claims.email ?? fallbackEmail,
    firstName: (claims.given_name ?? '').trim(),
    isAdmin: groups.includes('admins'),
  };
}
